// Constants
import { DynamicFilterField } from "../../constants/DynamicFilterConstants";
// Utilities
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
/**
 * Set of filters for an entity that are used together in a logical "AND" fashion
 */ export class EntityFilter {
    /**
     * Get the active filter count
     */ get filterCount() {
        let count = 0;
        this.filters.forEach((filter)=>{
            if (filter.active === false) {
                return;
            }
            // Dynamic filters carry task IDs as values (a result set, not user selections),
            // so count the filter itself as 1 instead of counting each task ID.
            count += filter.field === DynamicFilterField ? 1 : filter.values.length;
        });
        return count;
    }
    /**
     * Get the active filter count for a particular field
     * @param filterField Filter key field
     */ getFieldFilterCount(filterField) {
        const index = this.indexOfFilter(filterField);
        if (index < 0 || this.filters[index].active === false) {
            return 0;
        }
        return this.filters[index].values.length;
    }
    /**
     * Replace an old filter in the set with a new one
     * @param filterField Filter key field
     * @param newFilter New filter
     */ replaceFilter(filterField, newFilter) {
        const clone = this.getClone();
        const oldFilter = clone.filters.filter((filter)=>{
            return filter.field === filterField;
        });
        let newFilters = clone.filters;
        if (isEmpty(oldFilter)) {
            newFilters.push(newFilter);
        } else {
            newFilters = clone.filters.map((filter)=>{
                if (filter.field === filterField) {
                    return newFilter;
                } else {
                    return filter;
                }
            });
        }
        return new EntityFilter(newFilters);
    }
    /**
     * Remove a filter from the set
     * @param filterField Filter key field
     */ removeFilter(filterField) {
        const clone = this.getClone();
        const index = clone.indexOfFilter(filterField);
        if (index >= 0) {
            clone.filters.splice(index, 1);
        }
        return clone;
    }
    /**
     * Set the active state of a filter in the set.
     * Returns a new EntityFilter with the filter's active state changed.
     * @param filterField Filter key field
     * @param active Whether the filter should be active
     */ setFilterActive(filterField, active) {
        const clone = this.getClone();
        const index = clone.indexOfFilter(filterField);
        if (index < 0) {
            return clone;
        }
        const filter = clone.filters[index];
        clone.filters[index] = new Filter(filter.field, filter.values, filter.label, active);
        return clone;
    }
    /**
     * Determine whether a filter with the specific value and field exists in the filter set
     * @param filterField Filter key field
     * @param value Value to toggle
     */ doesFilterValueExist(filterField, value) {
        const index = this.indexOfFilter(filterField);
        return index >= 0 && this.filters[index].values.indexOf(value) >= 0;
    }
    /**
     * Return a new EntityFilter with the value toggled for a specific field in the filter set
     * @param filterField Filter key field
     * @param value Value to toggle
     */ toggleFilterValue(filterField, value) {
        if (this.doesFilterValueExist(filterField, value)) {
            return this.removeFilterValue(filterField, value);
        } else {
            return this.addFilterValue(filterField, value);
        }
    }
    /**
     * Add a value to the filter for a specific field in the filter set
     * @param filterField Filter key field
     * @param value Value to add
     */ addFilterValue(filterField, value) {
        const clone = this.getClone();
        const index = clone.indexOfFilter(filterField);
        if (index >= 0) {
            clone.filters[index].values.push(value);
        } else {
            clone.filters.push(new Filter(filterField, [
                value
            ]));
        }
        return clone;
    }
    /**
     * Remove a value from the filter for a specific field in the filter set
     * @param filterField Filter key field
     * @param value Value to remove
     */ removeFilterValue(filterField, value) {
        const clone = this.getClone();
        const index = clone.indexOfFilter(filterField);
        if (index >= 0) {
            const valueIndex = clone.filters[index].values.indexOf(value);
            if (valueIndex >= 0) {
                clone.filters[index].values.splice(valueIndex, 1);
            }
        }
        if (clone.filters[index].values.length === 0) {
            // Filter has become defunct with no values, so remove it
            clone.filters.splice(index, 1);
        }
        return clone;
    }
    getClone() {
        return cloneDeep(this);
    }
    /**
     * Get the index of a filter in the filter set with the field
     * @param filterField Filter key field
     */ indexOfFilter(filterField) {
        let index = -1;
        for(let i = 0; i < this.filters.length; i++){
            const filter = this.filters[i];
            if (filter.field === filterField) {
                index = i;
            }
        }
        return index;
    }
    /**
     * Initializes a new instance of the "FilterSet" entity.
     * @param [filters] Set of filters for the entity
     */ constructor(filters){
        this.filters = [];
        if (filters != null) {
            this.filters = filters;
        }
    }
}
/**
 * Filter object that encapsulates the data for a filtering operation
 */ export class Filter {
    /**
     * Initializes a new instance of the "Filter" entity.
     * @param field Field of the E to filter on
     * @param values Set of values of the field that the filtered entities must satisfy in a logical 'OR' fashion
     * @param label Optional human-readable description of the filter
     * @param active Whether the filter is actively applied (default: true)
     */ constructor(field, values, label, active = true){
        if (isEmpty(values)) {
            throw new Error("InvalidArgumentException: values cannot be empty");
        }
        this.field = field;
        this.values = values;
        this.label = label;
        this.active = active;
    }
}
